import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { QrCode, Phone, Car, Loader2, Check, Lock, ShieldAlert } from 'lucide-react';
import { API } from '../App';
import { sendOTP, verifyOTP } from '../lib/qr-pool-api';
import AppTopHeader from '../components/AppTopHeader';

const ClaimQRPage = () => {
  const { qrId } = useParams();
  const navigate = useNavigate();
  
  // Multi-step state
  const [step, setStep] = useState('phone'); // phone, otp, vehicle, success
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [successData, setSuccessData] = useState(null);
  
  // OTP timer state
  const [otpTimer, setOtpTimer] = useState(0);
  const [otpAttempts, setOtpAttempts] = useState(0);
  const [token, setToken] = useState(null); // JWT token after OTP verification

  // OTP timer effect
  useEffect(() => {
    if (otpTimer <= 0) return;
    const interval = setInterval(() => setOtpTimer(t => t - 1), 1000);
    return () => clearInterval(interval);
  }, [otpTimer]);

  // Validate phone format
  const validatePhone = (phoneStr) => {
    return /^[\d+\-() ]{10,}$/.test(phoneStr.trim());
  };

  // Step 1: Send OTP
  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!phone.trim()) {
      setErrors({ phone: 'Phone number is required' });
      return;
    }
    if (!validatePhone(phone)) {
      setErrors({ phone: 'Please enter a valid phone number' });
      return;
    }

    setLoading(true);
    try {
      await sendOTP(phone);
      toast.success('OTP sent to your phone number');
      setStep('otp');
      setOtpTimer(300); // 5 minutes
      setOtpAttempts(0);
      setErrors({});
    } catch (error) {
      const errorMsg = error.detail || 'Failed to send OTP';
      setErrors({ phone: errorMsg });
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp.trim()) {
      setErrors({ otp: 'OTP is required' });
      return;
    }
    if (!/^\d{6}$/.test(otp.trim())) {
      setErrors({ otp: 'OTP must be 6 digits' });
      return;
    }

    // Check attempts
    if (otpAttempts >= 3) {
      setErrors({ otp: 'Too many failed attempts. Request a new OTP.' });
      toast.error('Too many failed OTP attempts');
      return;
    }

    setLoading(true);
    try {
      const response = await verifyOTP(phone, otp);
      
      // Store token for next step
      setToken(response.token);
      
      toast.success('Phone number verified');
      setStep('vehicle');
      setErrors({});
    } catch (error) {
      const errorMsg = error.detail || 'Invalid OTP';
      setOtpAttempts(prev => prev + 1);
      setErrors({ otp: errorMsg });
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Claim QR with vehicle number
  const handleClaimQR = async (e) => {
    e.preventDefault();
    
    // Validate vehicle number
    if (!vehicleNumber.trim()) {
      setErrors({ vehicleNumber: 'Vehicle registration number is required' });
      return;
    }
    if (vehicleNumber.trim().length < 3) {
      setErrors({ vehicleNumber: 'Vehicle number must be at least 3 characters' });
      return;
    }
    if (!termsAccepted) {
      setErrors({ terms: 'You must accept the Terms & Conditions to continue' });
      return;
    }

    setLoading(true);
    try {
      const axiosConfig = token ? {
        headers: { Authorization: `Bearer ${token}` }
      } : {};

      const response = await axios.post(
        `${API}/q/${qrId}/claim`,
        { phone: phone.trim(), vehicle_number: vehicleNumber.trim() },
        axiosConfig
      );

      toast.success('QR code successfully activated!');
      setSuccessData({
        vehicleNumber: vehicleNumber.toUpperCase(),
        expiryDate: response.data.expiry_date
      });
      setStep('success');
      setErrors({});
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to claim QR code';
      toast.error(errorMsg);
      setErrors({ submit: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  // Success screen
  if (step === 'success') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Activated!</h1>
          <p className="text-gray-600 mb-2">
            Your QR code is now activated for
          </p>
          <p className="text-2xl font-bold text-[#0DB504] mb-4">
            {successData?.vehicleNumber}
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Valid until {new Date(successData?.expiryDate).toLocaleDateString()}
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full bg-[#0DB504] hover:bg-[#0a9e03] text-white font-semibold py-2 px-4 rounded-lg transition"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Render appropriate step
  const renderPhoneStep = () => (
    <form onSubmit={handleSendOTP} className="space-y-4">
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4" />
            Mobile Number
          </div>
        </label>
        <input
          type="tel"
          value={phone}
          onChange={(e) => {
            setPhone(e.target.value);
            if (errors.phone) setErrors(prev => ({ ...prev, phone: '' }));
          }}
          placeholder="+91 98765 43210"
          className={`w-full px-4 py-2 rounded-lg border-2 focus:outline-none transition ${
            errors.phone
              ? 'border-red-400 bg-red-50 focus:border-red-500'
              : 'border-gray-300 focus:border-indigo-500'
          }`}
        />
        {errors.phone && <p className="text-red-600 text-sm mt-1">{errors.phone}</p>}
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full bg-[#0DB504] hover:bg-[#0a9e03] disabled:bg-gray-400 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Sending OTP...
          </>
        ) : (
          <>
            <Phone className="w-4 h-4" />
            Send OTP
          </>
        )}
      </button>
    </form>
  );

  const renderOTPStep = () => (
    <form onSubmit={handleVerifyOTP} className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
        <p className="text-sm text-blue-700 mb-1">OTP sent to</p>
        <div className="flex items-center justify-between">
          <span className="text-base font-bold text-slate-900" style={{ fontFamily: 'Barlow' }}>{phone}</span>
          <button
            type="button"
            onClick={() => { setStep('phone'); setOtp(''); setErrors({}); }}
            className="text-sm text-[#0DB504] hover:underline font-medium"
          >
            Change
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4" />
            Enter 6-Digit OTP
          </div>
        </label>
        <input
          type="text"
          value={otp}
          onChange={(e) => {
            setOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
            if (errors.otp) setErrors(prev => ({ ...prev, otp: '' }));
          }}
          placeholder="000000"
          maxLength="6"
          className={`w-full px-4 py-3 text-2xl text-center font-mono rounded-lg border-2 focus:outline-none transition ${
            errors.otp
              ? 'border-red-400 bg-red-50 focus:border-red-500'
              : 'border-gray-300 focus:border-indigo-500'
          }`}
        />
        {errors.otp && <p className="text-red-600 text-sm mt-1">{errors.otp}</p>}
      </div>

      <div className="text-center text-sm text-gray-600">
        {otpTimer > 0 ? (
          <p>OTP expires in {Math.floor(otpTimer / 60)}:{String(otpTimer % 60).padStart(2, '0')}</p>
        ) : (
          <button
            type="button"
            onClick={handleSendOTP}
            className="text-[#0DB504] hover:underline font-medium"
          >
            Resend OTP
          </button>
        )}
      </div>

      <button
        type="submit"
        disabled={loading || otp.length !== 6}
        className="w-full bg-[#0DB504] hover:bg-[#0a9e03] disabled:bg-gray-400 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Verifying...
          </>
        ) : (
          <>
            <ShieldAlert className="w-4 h-4" />
            Verify OTP
          </>
        )}
      </button>

      <button
        type="button"
        onClick={() => {
          setStep('phone');
          setOtp('');
          setErrors({});
        }}
        className="w-full text-[#0DB504] hover:text-[#0a9e03] font-medium py-2"
      >
        Back
      </button>
    </form>
  );

  const renderVehicleStep = () => (
    <form onSubmit={handleClaimQR} className="space-y-4">
      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
        <p className="text-sm text-green-700">
          ✓ Phone number verified
        </p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          <div className="flex items-center gap-2">
            <Car className="w-4 h-4" />
            Vehicle Registration Number
          </div>
        </label>
        <input
          type="text"
          value={vehicleNumber}
          onChange={(e) => {
            setVehicleNumber(e.target.value.toUpperCase());
            if (errors.vehicleNumber) setErrors(prev => ({ ...prev, vehicleNumber: '' }));
          }}
          placeholder="e.g., KA01AB1234"
          className={`w-full px-4 py-2 rounded-lg border-2 focus:outline-none transition uppercase ${
            errors.vehicleNumber
              ? 'border-red-400 bg-red-50 focus:border-red-500'
              : 'border-gray-300 focus:border-indigo-500'
          }`}
        />
        {errors.vehicleNumber && (
          <p className="text-red-600 text-sm mt-1">{errors.vehicleNumber}</p>
        )}
      </div>

      {errors.submit && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-red-600 text-sm">{errors.submit}</p>
        </div>
      )}

      <div className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50">
        <input
          type="checkbox"
          id="terms-accept"
          checked={termsAccepted}
          onChange={(e) => {
            setTermsAccepted(e.target.checked);
            if (errors.terms) setErrors(prev => ({ ...prev, terms: '' }));
          }}
          className="mt-0.5 w-4 h-4 accent-[#0DB504] cursor-pointer flex-shrink-0"
        />
        <label htmlFor="terms-accept" className="text-sm text-slate-700 cursor-pointer leading-snug" style={{ fontFamily: 'Barlow' }}>
          I agree to the{' '}
          <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" className="text-black font-semibold underline underline-offset-2">Terms &amp; Conditions</a>
          {' '}and{' '}
          <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-black font-semibold underline underline-offset-2">Privacy Policy</a>
        </label>
      </div>
      {errors.terms && <p className="text-red-600 text-sm -mt-2">{errors.terms}</p>}

      <button
        type="submit"
        disabled={loading || !termsAccepted}
        className="w-full bg-[#0DB504] hover:bg-[#0a9e03] disabled:bg-gray-400 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Activating...
          </>
        ) : (
          <>
            <QrCode className="w-4 h-4" />
            Activate QR Code
          </>
        )}
      </button>

      <button
        type="button"
        onClick={() => {
          setStep('otp');
          setVehicleNumber('');
          setErrors({});
        }}
        className="w-full text-[#0DB504] hover:text-[#0a9e03] font-medium py-2"
      >
        Back
      </button>
    </form>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <AppTopHeader backLabel="Back" />
      <div className="flex items-center justify-center p-4 py-8">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">

        <h1 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Barlow' }}>
          {step === 'phone' ? 'Activate QR' : step === 'otp' ? 'Verify OTP' : 'Register Vehicle'}
        </h1>

        {/* Progress indicator */}
        <div className="flex gap-2 mb-6">
          <div className={`flex-1 h-1 rounded-full transition ${
            ['phone', 'otp', 'vehicle'].includes(step) ? 'bg-[#0DB504]' : 'bg-gray-300'
          }`} />
          <div className={`flex-1 h-1 rounded-full transition ${
            ['otp', 'vehicle'].includes(step) ? 'bg-[#0DB504]' : 'bg-gray-300'
          }`} />
          <div className={`flex-1 h-1 rounded-full transition ${
            step === 'vehicle' ? 'bg-[#0DB504]' : 'bg-gray-300'
          }`} />
        </div>

        {/* QR ID Display */}
      <div className="bg-green-50 rounded-lg p-4 mb-4 border-2 border-green-200">
          <p className="text-xs text-gray-600 text-center mb-1">QR Code ID</p>
          <p className="text-xl font-mono font-bold text-slate-900 text-center break-all">{qrId}</p>
        </div>

        {/* Register context — shown only on phone step */}
        {step === 'phone' && (
          <div className="bg-amber-50 rounded-lg p-4 mb-6 border-l-4 border-amber-400">
            <p className="text-sm font-semibold text-amber-700 mb-1">Ready to Register</p>
            <p className="text-sm text-gray-700">
              This QR code hasn't been registered yet. Register your vehicle to start using this QR code.
            </p>
          </div>
        )}

        {/* Step content */}
        {step === 'phone' && renderPhoneStep()}
        {step === 'otp' && renderOTPStep()}
        {step === 'vehicle' && renderVehicleStep()}

        {/* Info Box */}
        <div className="mt-6 bg-blue-50 rounded-lg p-4 border border-blue-200">
          <p className="text-sm text-blue-700">
            <strong>ℹ️ Secure:</strong> Your phone number is verified via OTP for security.
          </p>
        </div>

          {/* Footer */}
          <div className="mt-6 text-center text-sm text-gray-500 border-t pt-4">
            <p>Questions? Contact support@sideo.in</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaimQRPage;
